/* Idea: When supporting linewrap, keep spans of 100(?) lines that computes stats of the contained lines.
   One such stat could be relative to the current screen width, and compute which smaller or larger width
   would cause a line in the span to break or collapse into more or fewer screen-lines, so the entire span
   can skip recomputing if view changes width inside that range.
   Also entire spans can skip recomputing per-line stuff when spans before or after are modified. */

const SCROLL_MARGIN_LINES = 3; // Number of lines to keep visible above/below cursor when scrolling into view
const TAB_STOP = 4;
export type Pos = { line: number; ch: number };
export type Selection = { anchor: Pos; head: Pos };

type UndoRedoOp = {
    from: Pos;
    to: Pos;
    content: string;
};
type UndoRedo = {
    operations: UndoRedoOp[];
    selectionBefore: Selection | null;
    selectionAfter: Selection | null;
    label?: string;
    timestamp?: number;
};

// todo: core should expose a virtualized view of the screen, and convert screenspace<->textspace
// todo: core should control the gutter, and have a setting for which gutter-elements to show
// todo: core should support line wrap (variable screen-height lines)
// todo: core should control and expose tokenization
// todo: folding
// todo: replace Selection{anchor,head} and getNormalizedSelection with an always-normalized Range{from,to}. Instead look at the cursor to determine what is the head.
// todo: insert/overwrite

// todo: unit tests

// todo: search/replace (that doesn't support searching across newlines?)
// todo: doubleclick to select word
// todo: jump between braces
// todo: autoindent

// todo: there's some bugs with cursor after redo, and perhaps missing selection implementation

// https://coredumped.dev/2023/08/09/text-showdown-gap-buffers-vs-ropes/
/** Abstracts the operations that will change if we switch to using a piece table / gap buffer / ropes */
class TextBuffer {
    lines: string[] = [""];

    setContent(content: string) {
        this.lines = stringToLines(content);
    }

    get lineCount() {
        return this.lines.length;
    }

    getLine(line: number): string {
        return this.lines[line] ?? "";
    }

    getLineLength(line: number): number {
        return this.lines[line]?.length ?? 0;
    }

    getLineSlice(line: number, from: number, to?: number): string {
        return (to === undefined ? this.lines[line]?.slice(from) : this.lines[line]?.slice(from, to)) ?? "";
    }

    getRange(from: Pos, to: Pos): string {
        if (from.line === to.line) {
            // Single line selection
            return this.getLineSlice(from.line, from.ch, to.ch);
        } else {
            // Multi-line selection
            const result: string[] = [];
            for (let i = from.line; i <= to.line; i++) {
                if (i === from.line) {
                    result.push(this.getLineSlice(i, from.ch));
                } else if (i === to.line) {
                    result.push(this.getLineSlice(i, 0, to.ch));
                } else {
                    result.push(this.getLine(i));
                }
            }
            return result.join("\n");
        }
    }

    replaceRange(from: Pos, to: Pos, content: string): Pos {
        if (isEmpty(from, to, content)) return to;

        let finalPos: Pos;
        const replacementLines = stringToLines(content);
        const newLines = [...this.lines];
        const firstLine = this.lines[from.line];
        const lastLine = this.lines[to.line];
        const beforeText = firstLine.slice(0, from.ch);
        const afterText = lastLine.slice(to.ch);

        if (replacementLines.length === 1) {
            // Single line insertion/replacement
            newLines[from.line] = beforeText + replacementLines[0] + afterText;

            finalPos = { line: from.line, ch: from.ch + replacementLines[0].length };
        } else {
            // Multi-line insertion/replacement
            newLines[from.line] = beforeText + replacementLines[0];

            for (let i = 1; i < replacementLines.length - 1; i++) {
                newLines.splice(from.line + i, 0, replacementLines[i]);
            }

            const lastLine = replacementLines[replacementLines.length - 1];
            newLines.splice(from.line + replacementLines.length - 1, 0, lastLine + afterText);

            finalPos = {
                line: from.line + replacementLines.length - 1,
                ch: lastLine.length
            };
        }

        // Remove original selected lines (if multi-line selection)
        if (to.line > from.line) {
            newLines.splice(from.line + replacementLines.length, to.line - from.line);
        }

        this.lines = newLines;
        return finalPos;
    }
}

export class TextEditorCore {
    buffer = new TextBuffer();
    cursor: Pos = { line: 0, ch: 0 };
    selection: Selection | null = null;
    linesPerPage: number = 10; // todo
    private isMouseDown: boolean = false;
    private history: UndoRedo[] = [];
    private historyIndex: number | null = null;
    private transaction: UndoRedo | null = null;

    private setCursor(cursor: Pos) {
        this.cursor = cursor;
    }

    getSelectedText(): string {
        const sel = this.getNormalizedSelection();
        if (!sel) return "";
        return this.buffer.getRange(sel.anchor, sel.head);
    }

    setSelection(sel: Selection | null) {
        this.selection = sel;
    }

    getNormalizedSelection() {
        if (!this.selection) return null;
        const { anchor, head } = this.selection;
        const anchorFirst = anchor.line < head.line || (anchor.line === head.line && anchor.ch <= head.ch);
        return anchorFirst ? this.selection : { anchor: head, head: anchor };
    }

    // #region Navigation

    moveCursorTo(line: number, ch: number, extend = false): void {
        if (line < 0) { line = 0; ch = 0; }
        if (line >= this.buffer.lineCount) { line = this.buffer.lineCount - 1; ch = this.buffer.getLineLength(line); }
        const lineText = this.buffer.getLine(line);
        if (ch < 0) ch = 0;
        if (ch > lineText.length) ch = lineText.length;

        this.setSelection(!extend ? null : {
            anchor: this.selection?.anchor || this.cursor,
            head: { line, ch },
        });

        this.setCursor({ line, ch });
    }

    moveCursorBy(dl: number, dc: number, extend = false): void {
        let newLine = this.cursor.line + dl;
        let newCh = this.cursor.ch + dc;

        // Handle wrapping to previous/next line
        if (dl === 0) {
            if (newCh < 0 && this.cursor.line > 0) {
                // Move to end of previous line
                newLine = this.cursor.line - 1;
                newCh = this.buffer.getLineLength(newLine);
            } else if (newCh > this.buffer.getLineLength(this.cursor.line) && this.cursor.line < this.buffer.lineCount - 1) {
                // Move to start of next line
                newLine = this.cursor.line + 1;
                newCh = 0;
            }
        }

        this.moveCursorTo(newLine, newCh, extend);
    };

    moveByWord(pos: Pos, direction: "left" | "right"): Pos {
        const lineText = this.buffer.getLine(pos.line);
        let ch = pos.ch;
        const isRight = direction === "right";

        // Helper to get char at position (handles both directions)
        const charAt = (offset: number) => lineText[isRight ? ch + offset : ch - 1 - offset];
        const atBoundary = isRight ? ch >= lineText.length : ch === 0;

        // Handle line wrapping
        if (atBoundary) {
            if (isRight && pos.line < this.buffer.lineCount - 1) {
                return { line: pos.line + 1, ch: 0 };
            }
            if (!isRight && pos.line > 0) {
                return { line: pos.line - 1, ch: this.buffer.getLineLength(pos.line - 1) };
            }
            return pos; // At document boundary
        }

        // Special case for left: move back one if inside a word
        if (!isRight && charType(charAt(0)) === "word") {
            ch--;
        }

        let type = charType(charAt(0) || " ");

        // 1. Skip current run of same-type chars
        while ((isRight ? ch < lineText.length : ch > 0) && charType(charAt(0)) === type) {
            ch += isRight ? 1 : -1;
        }

        // 2. For right movement: skip punctuation/whitespace until we hit a word char
        if (isRight) {
            while (ch < lineText.length && charType(charAt(0)) !== "word") {
                ch++;
            }
        }

        return { line: pos.line, ch };
    }

    // #endregion

    // #region Undo/redo

    /** All text-editing must go through this method. Records an undo/redo history. */
    replaceRange(from: Pos, to: Pos, content: string): Pos {
        if (isEmpty(from, to, content)) return to;
        content = content.replace(/\r/g, "") // Normalize line endings (remove \r characters)
        this._appendUndoHistory(from, to, content);
        return this.buffer.replaceRange(from, to, content);
    }

    clearHistory() {
        this.transaction = null;
        this.history = [];
        this.historyIndex = null;
    }

    beginTransaction() {
        this.endTransaction();
        this.transaction = {
            operations: [],
            selectionBefore: this.selection ? { ...this.selection } : null,
            selectionAfter: null,
        }
    }

    endTransaction() {
        if (!this.transaction) return;
        if (this.transaction.operations.length > 0) {
            this.transaction.selectionAfter = this.selection;
            this.history.push(this.transaction);
        }
        this.transaction = null;
    }

    private _appendUndoHistory(from: Pos, to: Pos, content: string) {
        // If we're not at the end of history (user has undone some operations), truncate the history from this point forward
        const currentIndex = this.historyIndex ?? this.history.length;
        if (currentIndex < this.history.length) {
            this.history = this.history.slice(0, currentIndex);
        }
        this.historyIndex = null;

        // Append undo-history
        const oldContent = this.buffer.getRange(from, to);
        const finalPos = this._computeFinalPos(from, content);
        const op = { from, to: finalPos, content: oldContent };
        if (this.transaction) {
            // We are in a transaction, so add to that
            this.transaction.operations.push(op);
        }
        else if (this._isAdjacentOperation(op)) {
            // consecutive editing operations get merged
            this.history[this.history.length - 1].operations.push(op);
            this.history[this.history.length - 1].timestamp = new Date().getTime()
        } else {
            // add a new standalone operation
            this.history.push({
                operations: [op],
                selectionBefore: this.selection ? { ...this.selection } : null,
                selectionAfter: null, // todo
                timestamp: new Date().getTime()
            });
        }
    }

    private _isAdjacentOperation(op: UndoRedoOp) {
        if (this.history.length == 0) return false;
        const undoredo = this.history[this.history.length - 1];
        if (undoredo.operations.length == 0) return false;
        const lastop = undoredo.operations[undoredo.operations.length - 1];

        // Must be less than 5 seconds old
        if (undoredo.timestamp && new Date().getTime() - undoredo.timestamp > 5000) return false;

        const isInsert = op.content === "";
        const lastIsInsert = lastop.content === "";

        // Must be same operation type (both insert or both delete)
        if (isInsert !== lastIsInsert) return false;

        // Only merge single-character operations
        const isSingleChar = isInsert
            ? op.from.line === op.to.line && Math.abs(op.to.ch - op.from.ch) === 1
            : op.content.length === 1;
        const lastIsSingleChar = lastIsInsert
            ? lastop.from.line === lastop.to.line && Math.abs(lastop.to.ch - lastop.from.ch) === 1
            : lastop.content.length === 1;
        if (!isSingleChar || !lastIsSingleChar) return false;

        // Check if operations are adjacent
        if (isInsert) {
            // For insertions: new op must start where last op ended, and not be a newline
            return op.from.line === lastop.to.line && op.from.ch === lastop.to.ch && op.to.line === op.from.line;
        } else {
            // For deletions: must be backspace (continuous backward) or forward delete (continuous forward)
            const isBackspace = op.from.line === lastop.from.line && op.from.ch === lastop.from.ch - 1;
            const isForwardDelete = op.from.line === lastop.from.line && op.from.ch === lastop.from.ch;
            return isBackspace || isForwardDelete;
        }
    }

    /** Helper to compute the final position after a range replacement without performing the replacement. */
    private _computeFinalPos(from: Pos, content: string): Pos {
        // Normalize line endings
        const normalized = content.replace(/\r/g, "");

        // Find the last newline
        const lastNewlineIndex = normalized.lastIndexOf("\n");

        if (lastNewlineIndex === -1) {
            // Single line: position is on the same line, offset by content length
            return { line: from.line, ch: from.ch + normalized.length };
        } else {
            // Multi-line: count newlines and measure text after last newline
            const lineCount = normalized.split("\n").length - 1;
            const textAfterLastNewline = normalized.slice(lastNewlineIndex + 1);

            return {
                line: from.line + lineCount,
                ch: textAfterLastNewline.length
            };
        }
    }

    undo(): boolean {
        // If no history or we're at the beginning, nothing to undo
        if (this.history.length === 0) return false;
        const currentIndex = this.historyIndex ?? this.history.length;
        if (currentIndex === 0) return false;

        const undoIndex = currentIndex - 1;
        const undoOp = this.history[undoIndex];

        // Apply the operations in reverse order
        for (let i = undoOp.operations.length - 1; i >= 0; i--) {
            const op = undoOp.operations[i];
            const currentContent = this.buffer.getRange(op.from, op.to);
            const newTo = this.buffer.replaceRange(op.from, op.to, op.content);

            // Update the operation to store the inverse (for redo)
            op.content = currentContent;
            op.to = newTo;
        }

        // Restore the selection from before this operation
        if (undoOp.selectionBefore) {
            this.setSelection(undoOp.selectionBefore);
            this.setCursor(undoOp.selectionBefore.head);
        } else {
            this.setSelection(null);
            // Move cursor to the start of the undone range
            if (undoOp.operations.length > 0) {
                this.setCursor(undoOp.operations[0].from);
            }
        }

        this.historyIndex = undoIndex;
        return true;
    }

    redo(): boolean {
        // If no history or we're at the end, nothing to redo
        if (this.history.length === 0) return false;
        const currentIndex = this.historyIndex ?? this.history.length;
        if (currentIndex >= this.history.length) return false;

        const redoOp = this.history[currentIndex];

        // Apply the operations in forward order
        for (const op of redoOp.operations) {
            const currentContent = this.buffer.getRange(op.from, op.to);
            const newTo = this.buffer.replaceRange(op.from, op.to, op.content);

            // Update the operation to store the inverse (for undo again)
            op.content = currentContent;
            op.to = newTo;
        }

        // Restore the selection from after this operation
        if (redoOp.selectionAfter) {
            this.setSelection(redoOp.selectionAfter);
            this.setCursor(redoOp.selectionAfter.head);
        } else {
            this.setSelection(null);
            // Move cursor to where the operation ended
            if (redoOp.operations.length > 0) {
                const lastOp = redoOp.operations[redoOp.operations.length - 1];
                this.setCursor(lastOp.to);
            }
        }

        this.historyIndex = currentIndex + 1;
        if (this.historyIndex >= this.history.length) {
            this.historyIndex = null; // Back to the current state
        }

        return true;
    }

    // #endregion

    // #region Editing

    setContent(content: string) {
        this.buffer.setContent(content);
        this.clearHistory();
        this.setCursor({ line: 0, ch: 0 });
        this.selection = null;
    }

    /** Insert text at caret or replace selection with text (supports newlines) */
    typeAtCaret(text: string): Pos {
        const sel = this.getNormalizedSelection();
        const startPos = sel?.anchor ?? this.cursor;
        const endPos = sel?.head ?? this.cursor;

        const finalCursorPos = this.replaceRange(startPos, endPos, text);

        this.setCursor(finalCursorPos);
        this.setSelection(null);

        return finalCursorPos!;
    }

    indentLines(startLine: number, endLine: number): void {
        this.beginTransaction();

        // Capture state before changes
        const oldSelection = this.selection;
        const oldCursor = this.cursor;

        // Indent each line by inserting spaces at the beginning
        for (let i = startLine; i <= endLine; i++) {
            // Skip empty lines
            if (this.buffer.getLineLength(i) > 0) {
                this.replaceRange(
                    { line: i, ch: 0 },
                    { line: i, ch: 0 },
                    " ".repeat(TAB_STOP)
                );
            }
        }

        // Update selection if there is one: shift anchor and head if they're on affected lines and not at column 0
        if (oldSelection) {
            this.setSelection({
                anchor: oldSelection.anchor.line >= startLine && oldSelection.anchor.line <= endLine && oldSelection.anchor.ch > 0
                    ? { line: oldSelection.anchor.line, ch: oldSelection.anchor.ch + TAB_STOP }
                    : oldSelection.anchor,
                head: oldSelection.head.line >= startLine && oldSelection.head.line <= endLine && oldSelection.head.ch > 0
                    ? { line: oldSelection.head.line, ch: oldSelection.head.ch + TAB_STOP }
                    : oldSelection.head
            });
        }

        // Update cursor: shift if it's on an affected line and not at column 0
        if (oldCursor.line >= startLine && oldCursor.line <= endLine && oldCursor.ch > 0) {
            this.setCursor({ line: oldCursor.line, ch: oldCursor.ch + TAB_STOP });
        }

        this.endTransaction();
    }

    unindentLines(startLine: number, endLine: number): void {
        this.beginTransaction();

        // Capture state before changes
        const oldSelection = this.selection;
        const oldCursor = this.cursor;

        // Calculate spaces removed per line
        const spacesRemovedPerLine: Map<number, number> = new Map();

        // Remove leading whitespace from each line
        for (let i = startLine; i <= endLine; i++) {
            const line = this.buffer.getLine(i);
            let spacesToRemove = 0;
            for (let j = 0; j < Math.min(TAB_STOP, line.length); j++) {
                if (line[j] === " ") {
                    spacesToRemove++;
                } else if (line[j] === "\t") {
                    spacesToRemove = TAB_STOP;
                    break;
                } else {
                    break;
                }
            }
            spacesRemovedPerLine.set(i, spacesToRemove);

            if (spacesToRemove > 0) {
                this.replaceRange(
                    { line: i, ch: 0 },
                    { line: i, ch: spacesToRemove },
                    ""
                );
            }
        }

        // Update selection if there is one: shift anchor and head if they're on affected lines and not at column 0
        if (oldSelection) {
            this.setSelection({
                anchor: oldSelection.anchor.line >= startLine && oldSelection.anchor.line <= endLine && oldSelection.anchor.ch > 0
                    ? { line: oldSelection.anchor.line, ch: Math.max(0, oldSelection.anchor.ch - (spacesRemovedPerLine.get(oldSelection.anchor.line) || 0)) }
                    : oldSelection.anchor,
                head: oldSelection.head.line >= startLine && oldSelection.head.line <= endLine && oldSelection.head.ch > 0
                    ? { line: oldSelection.head.line, ch: Math.max(0, oldSelection.head.ch - (spacesRemovedPerLine.get(oldSelection.head.line) || 0)) }
                    : oldSelection.head
            });
        }

        // Update cursor: shift if it's on an affected line and not at column 0
        if (oldCursor.line >= startLine && oldCursor.line <= endLine && oldCursor.ch > 0) {
            this.setCursor({ line: oldCursor.line, ch: Math.max(0, oldCursor.ch - (spacesRemovedPerLine.get(oldCursor.line) || 0)) });
        }

        this.endTransaction();
    }

    // #endregion

    // #region Interaction

    handleKeyDown(key: string, shift: boolean, ctrl: boolean): boolean | Promise<void> {
        const { cursor, selection, linesPerPage } = this;

        if (ctrl) {
            switch (key) {
                case "c":
                    const text = this.getSelectedText();
                    if (text) {
                        navigator.clipboard.writeText(text);
                    }
                    return true;

                case "x":
                    const cutText = this.getSelectedText();
                    if (cutText) {
                        navigator.clipboard.writeText(cutText);
                        this.setCursor(this.typeAtCaret(""));
                    }
                    return true;

                case "v":
                    return navigator.clipboard
                        .readText()
                        .then(text => {
                            this.setCursor(this.typeAtCaret(text));
                        });

                case "a":
                    const lineCount = this.buffer.lineCount;
                    this.setSelection({
                        anchor: { line: 0, ch: 0 },
                        head: { line: this.buffer.lineCount - 1, ch: this.buffer.getLineLength(lineCount - 1) }
                    });
                    this.setCursor({ line: this.selection!.head.line, ch: this.selection!.head.ch });
                    return true;

                case "z":
                    this.undo();
                    return true;

                case "y":
                    this.redo();
                    return true;
            }
        }

        switch (key) {
            case "ArrowUp":
                this.moveCursorBy(-1, 0, shift);
                break;

            case "ArrowDown":
                this.moveCursorBy(1, 0, shift);
                break;

            case "ArrowLeft":
                if (ctrl) {
                    const newPos = this.moveByWord(cursor, "left");
                    this.moveCursorTo(newPos.line, newPos.ch, shift);
                } else {
                    this.moveCursorBy(0, -1, shift);
                }
                break;

            case "ArrowRight":
                if (ctrl) {
                    const newPos = this.moveByWord(cursor, "right");
                    this.moveCursorTo(newPos.line, newPos.ch, shift);
                } else {
                    this.moveCursorBy(0, 1, shift);
                }
                break;

            case "Home":
                {
                    let index = 0;
                    const line = this.buffer.getLine(cursor.line);
                    for (; line.length > index && line[index] == " "; index++);
                    if (cursor.ch == index) {
                        index = 0;
                    }
                    this.moveCursorTo(cursor.line, index, shift);
                }
                break;

            case "End":
                this.moveCursorTo(cursor.line, this.buffer.getLineLength(this.buffer.lineCount - 1), shift);
                break;

            case "PageUp":
                {
                    const targetLine = Math.max(0, cursor.line - linesPerPage);
                    this.moveCursorTo(targetLine, cursor.ch, shift);
                }
                break;

            case "PageDown":
                {
                    const targetLine = Math.min(this.buffer.lineCount - 1, cursor.line + linesPerPage);
                    this.moveCursorTo(targetLine, cursor.ch, shift);
                }
                break;

            case "Enter":
                this.setCursor(this.typeAtCaret("\n"));
                break;

            case "Backspace":
                {
                    const sel = this.getNormalizedSelection();
                    const startPos = sel ? sel.anchor : (cursor.ch > 0
                        ? { line: cursor.line, ch: cursor.ch - 1 }
                        : cursor.line > 0 ? { line: cursor.line - 1, ch: this.buffer.getLineLength(cursor.line - 1) } : cursor);
                    const endPos = sel ? sel.head : cursor;

                    const newCursor = this.replaceRange(startPos, endPos, "");
                    this.setCursor(newCursor);
                    this.setSelection(null);
                }
                break;

            case "Delete":
                {
                    const sel = this.getNormalizedSelection();
                    const startPos = sel ? sel.anchor : cursor;
                    const endPos = sel ? sel.head : (cursor.ch < this.buffer.getLineLength(cursor.line)
                        ? { line: cursor.line, ch: cursor.ch + 1 }
                        : cursor.line < this.buffer.lineCount - 1 ? { line: cursor.line + 1, ch: 0 } : cursor);

                    this.replaceRange(startPos, endPos, "");
                    this.setCursor(startPos);
                    this.setSelection(null);
                }
                break;

            case "Tab":
                const sel = this.getNormalizedSelection();
                if (sel) {
                    const isSingleLine = sel.anchor.line === sel.head.line;
                    // Check if this is a line-level operation (starts at column 0 or spans multiple lines)
                    const isLineLevel = !isSingleLine || sel.anchor.ch === 0;

                    // todo: extract a helper method that computes the range of lines eligible for a line-level operation on the selection. (empty result if none are eligible)
                    // this can be shared with the multiline commenting/uncommenting operation

                    if (isLineLevel) {
                        // Line-level indent/unindent
                        // If the head (caret) is at column 0, exclude the last line
                        const endLine = sel.head.ch !== 0 ? sel.head.line : sel.head.line - 1;

                        if (shift) {
                            this.unindentLines(sel.anchor.line, endLine);
                        } else {
                            this.indentLines(sel.anchor.line, endLine);
                        }
                    } else {
                        // Character-level selection within a line (not starting at column 0)
                        if (shift) {
                            // Shift+Tab: Just remove the selected text
                            this.setCursor(this.typeAtCaret(""));
                        } else {
                            // Tab: Replace selection with spaces to next tab stop
                            const tabSpaces = computeTabInsertion(selection!.anchor.ch);
                            this.setCursor(this.typeAtCaret(tabSpaces));
                        }
                    }
                } else {
                    // No selection (cursor only)
                    if (shift) {
                        // Shift+Tab: Remove up to TabStop spaces before cursor
                        const line = this.buffer.getLine(cursor.line);
                        let spacesToRemove = 0;
                        for (let i = cursor.ch - 1; i >= Math.max(0, cursor.ch - TAB_STOP); i--) {
                            if (i < 0 || line[i] !== " ") break;
                            spacesToRemove++;
                        }

                        // if there are no spaces to remove, we instead unindent the whole line
                        if (spacesToRemove == 0) {
                            this.unindentLines(cursor.line, cursor.line);
                            break;
                        }

                        const startPos = { line: cursor.line, ch: cursor.ch - spacesToRemove };
                        const endPos = cursor;
                        const newCursor = this.replaceRange(startPos, endPos, "");
                        this.setCursor(newCursor);
                    } else {
                        // Tab: Insert spaces to next tab stop
                        const tabSpaces = computeTabInsertion(cursor.ch);
                        const newCursor = this.replaceRange(cursor, cursor, tabSpaces);
                        this.setCursor(newCursor);
                    }
                }
                break;

            default:
                if (key.length === 1 && !ctrl) {
                    this.setCursor(this.typeAtCaret(key));
                    return true;
                }
                return false;
        }

        return true;
    }

    handleMouseDown(pos: Pos, shift: boolean, ctrl: boolean): boolean {
        this.isMouseDown = true;

        if (shift) {
            // Shift-click: extend selection from current cursor/anchor
            this.setSelection({
                anchor: this.selection?.anchor || this.cursor,
                head: pos
            });
        } else {
            // Normal click: start new selection
            this.setSelection({ anchor: pos, head: pos });
        }

        this.setCursor(pos);
        return true;
    }

    handleMouseMove(pos: Pos) {
        if (!this.isMouseDown) return;

        this.setSelection(this.selection ? { ...this.selection, head: pos } : null);
        this.setCursor(pos);
    }

    handleMouseUp() {
        this.isMouseDown = false;

        if (!this.selection) return;

        const same = this.selection.anchor.line === this.selection.head.line &&
            this.selection.anchor.ch === this.selection.head.ch;
        if (same) {
            this.setSelection(null);
        }
    }

    // #endregion
}

const charType = (ch: string): "word" | "punct" | "space" => {
    if (/[\s\u2000-\u206F\u2E00-\u2E7F]/.test(ch)) return "space";
    if (/[\w\d_$]/.test(ch)) return "word";
    return "punct";
};

const computeTabInsertion = (ch: number): string => {
    const spacesNeeded = TAB_STOP - (ch % TAB_STOP);
    return " ".repeat(spacesNeeded);
};

const isEmpty = /*#__PURE__*/ (from: Pos, to: Pos, content: string) => (from.line == to.line && from.ch == to.ch && content == "");

const stringToLines = (content: string) =>
    content
        .replace(/\r/g, "") // Normalize line endings (remove \r characters)
        .split("\n")
        .map(line => {
            // Convert tabs to spaces based on column position
            let index = line.indexOf("\t");
            while (index !== -1) {
                const spaces = computeTabInsertion(index);
                line = line.slice(0, index) + spaces + line.slice(index + 1);
                index = line.indexOf("\t", index);
            }
            return line;
        });